/**
 * Tagesraster der Ressourcen (Board „Belegung · Ressourcen“): eine Achse vom frühesten Öffnen bis
 * zum spätesten Schließen aller Ressourcen in halben Stunden; Zeit außerhalb des eigenen Fensters
 * schraffiert, Belegungen als Blöcke, eine gezogene Auswahl als Markierung.
 */
import type { ResourceOccupancyEntry, ResourceOccupancyRow } from '@/shared/api';
import { clockFromMinutes, SLOT_MINUTES } from '../resourceModel';
import {
  BLOCK_REASON_LABEL,
  ENTRY_STATE_LABEL,
  entryState,
  type EntryState,
} from './resourceCalendarModel';

export interface GridAxis {
  start: number;
  end: number;
  slots: number;
}

/** Ohne Ressourcen gibt es keine Achse – das Raster zeigt dann nichts statt eines erfundenen Tages. */
export function gridAxis(rows: readonly ResourceOccupancyRow[]): GridAxis | null {
  if (rows.length === 0) return null;
  const start = Math.min(...rows.map((row) => row.opensAtMinute));
  const end = Math.max(...rows.map((row) => row.closesAtMinute));
  return end > start ? { start, end, slots: (end - start) / SLOT_MINUTES } : null;
}

/** Lage eines Zeitraums auf der Achse in Prozent. */
export function span(axis: GridAxis, from: number, to: number) {
  const width = axis.end - axis.start;
  return { left: ((from - axis.start) / width) * 100, width: ((to - from) / width) * 100 };
}

/** Zeit vor dem Öffnen und nach dem Schließen dieser Ressource. */
export function closedSpans(axis: GridAxis, row: ResourceOccupancyRow) {
  return [
    ...(row.opensAtMinute > axis.start ? [span(axis, axis.start, row.opensAtMinute)] : []),
    ...(row.closesAtMinute < axis.end ? [span(axis, row.closesAtMinute, axis.end)] : []),
  ];
}

export interface GridBlock {
  entry: ResourceOccupancyEntry;
  state: EntryState;
  label: string;
  time: string;
  left: number;
  width: number;
}

/** Wer oder was eine Zeit belegt: Grund und Notiz einer Sperre, sonst Gast und Buchungsnummer. */
export function entryWho(entry: ResourceOccupancyEntry): string {
  if (entry.kind === 'Block') {
    // Ein neuer Grund bleibt als Code sichtbar, statt still zu fehlen.
    const reason =
      entry.blockReason && (BLOCK_REASON_LABEL[entry.blockReason] ?? entry.blockReason);
    return [reason, entry.note].filter(Boolean).join(' · ');
  }
  return [entry.guestName, entry.bookingReference].filter(Boolean).join(' · ');
}

/**
 * Belegungen des Tages. Aufgehobene zuerst: sie liegen blass unter einer neuen Belegung derselben
 * Zeit, statt sie zu verdecken.
 */
export function gridBlocks(axis: GridAxis, row: ResourceOccupancyRow, date: string): GridBlock[] {
  return row.entries
    .filter((entry) => entry.date === date)
    .sort((a, b) => Number(b.status === 'Cancelled') - Number(a.status === 'Cancelled'))
    .map((entry) => {
      const state = entryState(entry);
      const who = entryWho(entry);
      return {
        entry,
        state,
        label: who ? `${ENTRY_STATE_LABEL[state]} · ${who}` : ENTRY_STATE_LABEL[state],
        time: `${clockFromMinutes(entry.startMinute)} – ${clockFromMinutes(entry.endMinute)}`,
        ...span(axis, entry.startMinute, entry.endMinute),
      };
    });
}

/** Die halbe Stunde unter einem Punkt der Zeile, begrenzt auf das Fenster der Ressource. */
export function slotAt(axis: GridAxis, row: ResourceOccupancyRow, fraction: number): number {
  const slot = Math.floor(Math.min(Math.max(fraction, 0), 0.9999) * axis.slots);
  const minute = axis.start + slot * SLOT_MINUTES;
  return Math.min(Math.max(minute, row.opensAtMinute), row.closesAtMinute - SLOT_MINUTES);
}

export interface GridSelection {
  resourceId: string;
  startMinute: number;
  endMinute: number;
}

/** Aus Anfang und Ende eines Zugs wird eine Auswahl ganzer halber Stunden. */
export function selectionOf(resourceId: string, anchor: number, current: number): GridSelection {
  return {
    resourceId,
    startMinute: Math.min(anchor, current),
    endMinute: Math.max(anchor, current) + SLOT_MINUTES,
  };
}
